import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Chat24Regular,
  DocumentBulletList24Regular,
  DataBarVertical24Regular,
  Shield24Regular,
  Headset24Regular,
  ArrowTrending24Regular,
  Money24Regular,
  DocumentText24Regular,
  Settings24Regular,
  ChevronDown16Regular,
  ChevronRight16Regular,
  Delete20Regular,
  Settings20Regular,
  Search20Regular,
  ArrowDownload20Regular,
  Briefcase24Regular,
  Add20Regular,
  Star20Regular,
  PanelLeft24Regular,
  Bot24Regular,
  Sparkle24Regular,
  Home24Regular,
  Apps24Regular,
  MoreHorizontal20Regular,
  Share20Regular,
  Edit20Regular,
} from "@fluentui/react-icons";
import { useShell, ShellAgent, PKB_AGENT_ID } from "../../context/ShellContext";
import { segregateItems } from "../../../components/Utils/utils";
import { historyDelete, historyDeleteAll, historySearch, historyRename, exportConversation, Conversation } from "../../../api";
import { ConfirmDialog } from "../ConfirmDialog/ConfirmDialog";
import { ExportModal, ExportAllModal } from "../../../components/ExportModal";
import { ShareDialog } from "../ShareDialog/ShareDialog";
import "./AgentSidebar.css";

const agentIconMap: Record<string, React.ReactElement> = {
  Chat: <Chat24Regular />,
  DocumentBulletList: <DocumentBulletList24Regular />,
  Briefcase: <Briefcase24Regular />,
  ClipboardTask: <Sparkle24Regular />,
  DataBarVertical: <DataBarVertical24Regular />,
  Shield: <Shield24Regular />,
  Headset: <Headset24Regular />,
  TrendingUp: <ArrowTrending24Regular />,
  Money: <Money24Regular />,
  DocumentText: <DocumentText24Regular />,
  Settings: <Settings24Regular />,
};

export function AgentSidebar() {
  const {
    agents,
    activeAgent,
    setActiveAgentById,
    chatHistory,
    refreshChatHistory,
    sidebarOpen,
    setSidebarOpen,
    setSidebarWidth,
    loading,
    setSettingsPanelOpen,
    currentConversationId,
    onNewChat,
    pendingRateConvId,
    setPendingRateConvId,
    canManagePrompts,
  } = useShell();

  const navigate = useNavigate();
  const location = useLocation();

  const [homeExpanded, setHomeExpanded] = useState(true);
  const [agentsExpanded, setAgentsExpanded] = useState(true);
  const [historyExpanded, setHistoryExpanded] = useState(true);
  const [chatSearchQuery, setChatSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<Conversation[] | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Resize drag state
  const isDraggingRef = useRef(false);
  const dragStartXRef = useRef(0);
  const dragStartWidthRef = useRef(0);
  const sidebarRef = useRef<HTMLElement>(null);

  const handleResizeMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isDraggingRef.current = true;
    dragStartXRef.current = e.clientX;
    dragStartWidthRef.current = sidebarRef.current
      ? sidebarRef.current.getBoundingClientRect().width
      : 280;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    const onMouseMove = (ev: MouseEvent) => {
      if (!isDraggingRef.current) return;
      const delta = ev.clientX - dragStartXRef.current;
      setSidebarWidth(dragStartWidthRef.current + delta);
    };
    const onMouseUp = () => {
      isDraggingRef.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  }, [setSidebarWidth]);

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState<{ type: "delete" | "deleteAll"; convId?: string; title?: string } | null>(null);

  // Export modal state (for individual chat export from sidebar)
  const [exportConvId, setExportConvId] = useState<string | null>(null);
  const [exportAllOpen, setExportAllOpen] = useState(false);

  // Three-dot menu state
  const [menuOpenConvId, setMenuOpenConvId] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Rename state
  const [renamingConvId, setRenamingConvId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const renameInputRef = useRef<HTMLInputElement | null>(null);

  // Copy-link toast notification
  const [showCopiedToast, setShowCopiedToast] = useState(false);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Share dialog state
  const [shareConv, setShareConv] = useState<{ id: string; title?: string } | null>(null);

  const groupedHistory = segregateItems(chatHistory);

  // Debounced server-side search (searches within message content and titles)
  const performSearch = useCallback(async (query: string) => {
    if (!query.trim()) {
      setSearchResults(null);
      setIsSearching(false);
      return;
    }
    setIsSearching(true);
    const results = await historySearch(query);
    setSearchResults(results ?? []);
    setIsSearching(false);
  }, []);

  const handleSearchChange = (value: string) => {
    setChatSearchQuery(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (!value.trim()) {
      setSearchResults(null);
      setIsSearching(false);
      return;
    }
    setIsSearching(true);
    searchTimerRef.current = setTimeout(() => performSearch(value), 350);
  };

  const filteredGroupedHistory = searchResults !== null
    ? [{ title: "Search results", entries: searchResults }]
    : groupedHistory;

  const isChatPage = location.pathname === "/";

  const handleNewChat = () => {
    if (onNewChat) onNewChat();
    navigate("/");
  };

  // PKB is the home page — exclude it from the agents sidebar list; only show accessible agents
  const sidebarAgents = agents.filter((a) => a.agentId !== PKB_AGENT_ID && a.hasAccess);

  const handleAgentClick = (agent: ShellAgent) => {
    setActiveAgentById(agent.agentId);
    navigate(`/agent/${agent.route}`);
  };

  const handleChatClick = (conversation: Conversation) => {
    navigate(`/?chat=${conversation.id}`);
  };

  const handleRateChat = (e: React.MouseEvent, convId: string) => {
    e.stopPropagation();
    setMenuOpenConvId(null);
    if (convId === currentConversationId) {
      // Already viewing this conversation — open dialog directly via pending
      setPendingRateConvId(convId);
    } else {
      // Navigate to the conversation first; Chat.tsx will open the dialog when loaded
      setPendingRateConvId(convId);
      navigate(`/?chat=${convId}`);
    }
  };

  const handleExportChat = (e: React.MouseEvent, conversation: Conversation) => {
    e.stopPropagation();
    setMenuOpenConvId(null);
    setExportConvId(conversation.id);
  };

  const handleDeleteChat = (e: React.MouseEvent, conversationId: string, title?: string) => {
    e.stopPropagation();
    setMenuOpenConvId(null);
    setConfirmDialog({ type: "delete", convId: conversationId, title });
  };

  const confirmDeleteChat = async () => {
    if (!confirmDialog?.convId) return;
    const convId = confirmDialog.convId;
    setConfirmDialog(null);
    try {
      await historyDelete(convId);
      await refreshChatHistory();
      if (convId === currentConversationId) {
        if (onNewChat) onNewChat();
        navigate("/");
      }
    } catch (err) {
      console.error("Failed to delete conversation:", err);
    }
  };

  const handleDeleteAllChats = () => {
    setConfirmDialog({ type: "deleteAll" });
  };

  const confirmDeleteAllChats = async () => {
    setConfirmDialog(null);
    try {
      const response = await historyDeleteAll();
      if (response.ok) {
        await refreshChatHistory();
        if (onNewChat) onNewChat();
        navigate("/");
      }
    } catch (err) {
      console.error("Failed to delete all conversations:", err);
    }
  };

  const handleExportAllChats = () => {
    setExportAllOpen(true);
  };

  // Three-dot menu toggle
  const toggleMenu = (e: React.MouseEvent, convId: string) => {
    e.stopPropagation();
    setMenuOpenConvId((prev) => (prev === convId ? null : convId));
  };

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpenConvId(null);
      }
    };
    if (menuOpenConvId) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpenConvId]);

  // Rename handlers
  const handleStartRename = (e: React.MouseEvent, conversation: Conversation) => {
    e.stopPropagation();
    setRenamingConvId(conversation.id);
    setRenameValue(conversation.title);
    setMenuOpenConvId(null);
    setTimeout(() => renameInputRef.current?.focus(), 0);
  };

  const handleRenameSubmit = async (convId: string) => {
    const trimmed = renameValue.trim();
    if (!trimmed || !convId) {
      setRenamingConvId(null);
      return;
    }
    try {
      await historyRename(convId, trimmed);
      await refreshChatHistory();
    } catch (err) {
      console.error("Failed to rename conversation:", err);
    }
    setRenamingConvId(null);
  };

  const handleRenameKeyDown = (e: React.KeyboardEvent, convId: string) => {
    if (e.key === "Enter") {
      handleRenameSubmit(convId);
    } else if (e.key === "Escape") {
      setRenamingConvId(null);
    }
  };

  // Share handlers
  const handleShareChat = (e: React.MouseEvent, conversation: Conversation) => {
    e.stopPropagation();
    setShareConv({ id: conversation.id, title: conversation.title });
    setMenuOpenConvId(null);
  };

  // Rate handler (from menu)
  const handleRateChatFromMenu = (e: React.MouseEvent, convId: string) => {
    e.stopPropagation();
    setMenuOpenConvId(null);
    handleRateChat(e, convId);
  };

  if (!sidebarOpen) return null;

  return (
    <aside ref={sidebarRef} className="agent-sidebar" aria-label="Agent sidebar" data-tour="sidebar">

      {/* Resize handle */}
      <div className="sidebar-resize-handle" onMouseDown={handleResizeMouseDown} aria-hidden="true" />

      {/* Home Section */}
      <div className="sidebar-section">
        <button
          className="sidebar-section-header"
          onClick={() => {
            if (onNewChat) onNewChat();
            navigate("/");
            if (canManagePrompts) setHomeExpanded(p => !p);
          }}
        >
          <Home24Regular className="sidebar-section-icon" />
          <span>Home</span>
          {canManagePrompts && (
            <span className="sidebar-section-chevron">
              {homeExpanded ? <ChevronDown16Regular /> : <ChevronRight16Regular />}
            </span>
          )}
        </button>

        {homeExpanded && canManagePrompts && (
          <div className="sidebar-section-content">
            <div
              className={`sidebar-item ${location.pathname === "/templates" ? "active" : ""}`}
              onClick={() => navigate("/templates")}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") navigate("/templates"); }}
            >
              <DocumentBulletList24Regular />
              <span>Manage Prompt Templates</span>
            </div>
          </div>
        )}
      </div>

      {/* Agents Section */}
      <div className="sidebar-section">
        <button
          className="sidebar-section-header"
          onClick={() => sidebarAgents.length > 0 && setAgentsExpanded(!agentsExpanded)}
          style={sidebarAgents.length === 0 ? { cursor: "default" } : undefined}
        >
          <Bot24Regular className="sidebar-section-icon" />
          <span>Agents</span>
          {sidebarAgents.length > 0 && (
            <span className="sidebar-section-chevron">
              {agentsExpanded ? <ChevronDown16Regular /> : <ChevronRight16Regular />}
            </span>
          )}
        </button>

        {agentsExpanded && (
          <div className="sidebar-section-content">
            {sidebarAgents.map((agent) => {
              const isActive = location.pathname === `/agent/${agent.route}`;

              return (
                <div
                  key={agent.agentId}
                  className={`sidebar-item agent-item ${isActive ? "active" : ""}`}
                  onClick={() => handleAgentClick(agent)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleAgentClick(agent); }}
                >
                  <span className="agent-item-icon">
                    {agentIconMap[agent.iconName] ?? <Chat24Regular />}
                  </span>
                  <span className="agent-item-name">{agent.name}</span>
                </div>
              );
            })}

          </div>
        )}
      </div>

      {/* Agents Store — primary menu item */}
      <div className="sidebar-section">
        <button
          className={`sidebar-section-header ${location.pathname === "/agents" ? "sidebar-section-header-active" : ""}`}
          data-tour="agent-store"
          onClick={() => navigate("/agents")}
        >
          <Apps24Regular className="sidebar-section-icon" />
          <span>Agent Store</span>
        </button>
      </div>

      {/* Chats Section */}
      <div className="sidebar-section chat-history-section" data-tour="chat-history">
        <div className={`sidebar-section-header-row ${isChatPage ? "sidebar-section-header-row-active" : ""}`}>
          <button
            className="sidebar-section-header"
            onClick={() => setHistoryExpanded(!historyExpanded)}
          >
            <Chat24Regular className="sidebar-section-icon" />
            <span>Chats</span>
          </button>
          <div className="sidebar-chats-actions">
            <button
              className="sidebar-action-btn"
              title="New chat"
              aria-label="New chat"
              data-tour="new-chat"
              onClick={handleNewChat}
            >
              <Add20Regular />
            </button>
            <button
              className="sidebar-action-btn"
              title="Export all chats"
              aria-label="Export all chats"
              onClick={handleExportAllChats}
              disabled={chatHistory.length === 0}
            >
              <ArrowDownload20Regular />
            </button>
            <button
              className="sidebar-action-btn sidebar-action-btn-danger"
              title="Delete all chats"
              aria-label="Delete all chats"
              onClick={handleDeleteAllChats}
              disabled={chatHistory.length === 0}
            >
              <Delete20Regular />
            </button>
            {isChatPage && (
              <button
                className="sidebar-action-btn"
                title="Chat settings"
                aria-label="Chat settings"
                onClick={() => setSettingsPanelOpen(true)}
              >
                <Settings20Regular />
              </button>
            )}
            <button
              className="sidebar-action-btn"
              title={historyExpanded ? "Collapse chats" : "Expand chats"}
              aria-label={historyExpanded ? "Collapse chats" : "Expand chats"}
              onClick={() => setHistoryExpanded(!historyExpanded)}
            >
              {historyExpanded ? <ChevronDown16Regular /> : <ChevronRight16Regular />}
            </button>
          </div>
        </div>

        {historyExpanded && (
          <div className="sidebar-section-content history-list">
            {/* Search bar */}
            <div className="sidebar-search">
              <Search20Regular className="sidebar-search-icon" />
              <input
                type="text"
                className="sidebar-search-input"
                placeholder="Search chats..."
                value={chatSearchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
              />
            </div>

            {loading && chatHistory.length === 0 && (
              <div className="history-loading">Loading...</div>
            )}

            {!loading && chatHistory.length === 0 && (
              <div className="history-empty">No chats yet</div>
            )}

            {chatSearchQuery && isSearching && (
              <div className="history-loading">Searching...</div>
            )}

            {chatSearchQuery && !isSearching && searchResults?.length === 0 && (
              <div className="history-empty">No matching chats</div>
            )}

            {filteredGroupedHistory.map(
              (group) =>
                group.entries.length > 0 && (
                  <div key={group.title} className="history-group">
                    <div className="history-group-title">{group.title}</div>
                    {group.entries.map((conversation) => (
                      <div
                        key={conversation.id}
                        className={`sidebar-item history-item ${location.pathname === "/" && conversation.id === currentConversationId ? "active" : ""} ${menuOpenConvId === conversation.id ? "menu-open" : ""}`}
                        onClick={() => renamingConvId !== conversation.id && handleChatClick(conversation)}
                        role="button"
                        tabIndex={0}
                        title={renamingConvId === conversation.id ? undefined : conversation.title}
                        onKeyDown={(e) => { if (renamingConvId !== conversation.id && (e.key === "Enter" || e.key === " ")) handleChatClick(conversation); }}
                      >
                        {renamingConvId === conversation.id ? (
                          <input
                            ref={renameInputRef}
                            className="history-rename-input"
                            value={renameValue}
                            onChange={(e) => setRenameValue(e.target.value)}
                            onBlur={() => handleRenameSubmit(conversation.id)}
                            onKeyDown={(e) => handleRenameKeyDown(e, conversation.id)}
                            onClick={(e) => e.stopPropagation()}
                            maxLength={256}
                          />
                        ) : (
                          <span className="history-item-title">
                            {conversation.title}
                          </span>
                        )}
                        <div className="history-item-actions">
                          <div className="sidebar-more-menu-wrapper" ref={menuOpenConvId === conversation.id ? menuRef : null}>
                            <button
                              className="history-action-btn"
                              title="More options"
                              onClick={(e) => toggleMenu(e, conversation.id)}
                            >
                              <MoreHorizontal20Regular />
                            </button>
                            {menuOpenConvId === conversation.id && (
                              <div className="sidebar-more-menu">
                                <button
                                  className="sidebar-more-menu-item"
                                  onClick={(e) => handleStartRename(e, conversation)}
                                >
                                  <Edit20Regular />
                                  Rename
                                </button>
                                <button
                                  className="sidebar-more-menu-item"
                                  onClick={(e) => handleShareChat(e, conversation)}
                                >
                                  <Share20Regular />
                                  Share
                                </button>
                                <button
                                  className="sidebar-more-menu-item"
                                  onClick={(e) => handleRateChatFromMenu(e, conversation.id)}
                                >
                                  <Star20Regular />
                                  Rate
                                </button>
                                <button
                                  className="sidebar-more-menu-item"
                                  onClick={(e) => handleExportChat(e, conversation)}
                                >
                                  <ArrowDownload20Regular />
                                  Export
                                </button>
                                <button
                                  className="sidebar-more-menu-item sidebar-more-menu-item-danger"
                                  onClick={(e) => handleDeleteChat(e, conversation.id, conversation.title)}
                                >
                                  <Delete20Regular />
                                  Delete
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )  
            )}
          </div>
        )}
      </div>

      {/* Collapse button at bottom-right */}
      <div className="sidebar-footer">
        <button
          className="sidebar-collapse-btn"
          title="Collapse sidebar"
          aria-label="Collapse sidebar"
          onClick={() => setSidebarOpen(false)}
        >
          <PanelLeft24Regular />
        </button>
      </div>

      {/* Copy link toast */}
      {showCopiedToast && (
        <div className="share-copied-toast">
          <Share20Regular />
          <span>Permalink to conversation copied to the clipboard. Everyone with this link can access this conversation! Please consider where you paste this link.</span>
        </div>
      )}

      {/* Delete single confirmation */}
      <ConfirmDialog
        isOpen={confirmDialog?.type === "delete"}
        title="Delete Conversation"
        message={confirmDialog?.title
          ? `Are you sure you want to delete "${confirmDialog.title}"? This action cannot be undone.`
          : "Are you sure you want to delete this conversation? This action cannot be undone."}
        confirmLabel="Yes, Delete"
        variant="danger"
        onConfirm={confirmDeleteChat}
        onCancel={() => setConfirmDialog(null)}
      />

      {/* Delete all confirmation */}
      <ConfirmDialog
        isOpen={confirmDialog?.type === "deleteAll"}
        title="Delete All Conversations"
        message={`Are you sure you want to delete all ${chatHistory.length} conversation(s)? This action cannot be undone.`}
        confirmLabel="Yes, Delete All"
        variant="danger"
        onConfirm={confirmDeleteAllChats}
        onCancel={() => setConfirmDialog(null)}
      />

      {/* Export conversation modal */}
      {exportConvId && (
        <ExportModal
          isOpen={!!exportConvId}
          onDismiss={() => setExportConvId(null)}
          conversationId={exportConvId}
        />
      )}

      {/* Export all conversations modal */}
      {exportAllOpen && (
        <ExportAllModal
          isOpen={exportAllOpen}
          onDismiss={() => setExportAllOpen(false)}
          conversations={chatHistory}
        />
      )}

      {/* Share conversation dialog */}
      {shareConv && (
        <ShareDialog
          isOpen={!!shareConv}
          conversationId={shareConv.id}
          conversationTitle={shareConv.title}
          onDismiss={() => setShareConv(null)}
        />
      )}
    </aside>
  );
}
